/*
  Dinowax dispatch date — re-checks the server-rendered date in the browser.

  WHY THIS EXISTS.
  Shopify caches rendered product HTML at the origin, and shopify.dev says of
  'now': "The timestamp will reflect the time that the Liquid was last rendered.
  Because of this, the timestamp might not be updated for every page view."
  Measured on this store: the first hit to a product page returns
  server-timing: render;dur=62, and subsequent hits return no render marker at
  all — they are served from cache. A random query string does not bust it. So a
  page rendered late Monday would keep telling a Wednesday shopper "Ships by
  Tue, Aug 18" until something else forces a re-render.

  The Liquid value stays as the no-JS and crawler fallback; this corrects it on
  load when it has drifted.

  WHOSE CLOCK.
  The parcel leaves the merchant's warehouse, so the shop's timezone decides the
  date — not the shopper's. Liquid has no shop.timezone object (checked: the
  shop object has no timezone property of any kind; the IANA name lives only on
  the Admin API), so it comes from a theme setting and is read here.

  The arithmetic runs on a UTC noon anchor built from the shop's local calendar
  date. That keeps it pure calendar maths: no DST transition can move a date
  when the anchor is midday and the steps are whole days.
*/
(function () {
  'use strict';

  /*
    Each format carries its own locale, deliberately. Passing `undefined` lets
    Intl follow the SHOPPER's locale, which produced "Tue 18 Aug" in a browser
    set to en-GB while Liquid had rendered "Tue, Aug 18" — so the text visibly
    rewrote itself on load and read differently for different visitors. The
    locale here is chosen to match the strftime pattern the section uses, so the
    server value and the refreshed value are identical.
  */
  var FORMATS = {
    short:    { locale: 'en-US', opts: { weekday: 'short', month: 'short', day: 'numeric' } },
    long:     { locale: 'en-US', opts: { weekday: 'long', month: 'long', day: 'numeric' } },
    monthday: { locale: 'en-US', opts: { month: 'short', day: 'numeric' } },
    daymonth: { locale: 'en-GB', opts: { day: 'numeric', month: 'short' } }
  };

  function shopParts(timezone) {
    // en-CA gives ISO-ish numeric parts, which parse without ambiguity.
    var fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', hour12: false
    });
    var parts = {};
    fmt.formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
    return {
      year: parseInt(parts.year, 10),
      month: parseInt(parts.month, 10),
      day: parseInt(parts.day, 10),
      hour: parseInt(parts.hour, 10) % 24
    };
  }

  function update(root) {
    var targets = root.querySelectorAll('[data-dw-dispatch-date]');
    if (!targets.length) return;

    var timezone = root.getAttribute('data-timezone');
    if (!timezone) return;

    var lead = parseInt(root.getAttribute('data-lead'), 10);
    if (isNaN(lead)) lead = 1;
    var skipWeekends = root.getAttribute('data-skip-weekends') === 'true';
    var useCutoff = root.getAttribute('data-use-cutoff') === 'true';
    var cutoff = parseInt(root.getAttribute('data-cutoff'), 10);
    var format = FORMATS[root.getAttribute('data-format')] || FORMATS.short;

    var now;
    try {
      now = shopParts(timezone);
    } catch (e) {
      // A bad timezone name throws. Leave the server-rendered date alone.
      return;
    }
    if (isNaN(now.year)) return;

    if (useCutoff && !isNaN(cutoff) && now.hour >= cutoff) lead += 1;

    // Noon UTC as a calendar anchor — see the note at the top.
    var target = new Date(Date.UTC(now.year, now.month - 1, now.day, 12));
    target.setUTCDate(target.getUTCDate() + lead);

    if (skipWeekends) {
      var guard = 0;
      while ((target.getUTCDay() === 0 || target.getUTCDay() === 6) && guard < 7) {
        target.setUTCDate(target.getUTCDate() + 1);
        guard++;
      }
    }

    // Format in UTC so the calendar date just computed is the one shown.
    var opts = { timeZone: 'UTC' };
    for (var k in format.opts) {
      if (Object.prototype.hasOwnProperty.call(format.opts, k)) opts[k] = format.opts[k];
    }

    var text;
    try {
      text = new Intl.DateTimeFormat(format.locale, opts).format(target);
    } catch (e) {
      return;
    }

    for (var i = 0; i < targets.length; i++) {
      if (targets[i].textContent.trim() !== text) targets[i].textContent = text;
    }
  }

  function init() {
    document.querySelectorAll('[data-dw-dispatch]').forEach(update);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Restored from bfcache after the page sat in a background tab overnight.
  window.addEventListener('pageshow', function (e) { if (e.persisted) init(); });

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelectorAll) e.target.querySelectorAll('[data-dw-dispatch]').forEach(update);
  });
})();
